import styles from './PageAssembly.module.scss';

import cx from 'classnames';
import React, { useEffect, useMemo, CSSProperties, HTMLAttributes } from 'react';

import { Assembly, ContentType, Page } from 'api/contentful/contentTypes.cloverdotcom';
import { HeroModule } from 'components/PageAssembly/HeroModule/HeroModule';
import { useSidebarOverride } from 'components/SidebarContext/UseSidebarOverride';
import { useContentEntriesContext } from 'components/ContentEntriesProvider/ContentEntriesContext';
import type { ReferencedEntry } from 'contentful';
import { Helmet } from 'react-helmet';
import { flattenContentfulData } from 'services/cms/flattenContentful';
import { targetView } from 'services/target';
import { getContentType } from 'utils/CloverdotcomEntryUtil';
import { componentKey, getField, isEntry } from 'utils/EntryUtil';
import { Environment } from 'utils/Environment';
import { ensureNoTrailingSlash } from 'utils/Urls';
import { applyEntryStyleAttributes } from '../EntryWrapper/EntryStyleWrapper';
import { NotFoundPage } from '../ErrorPage/NotFoundPage';
import { SomethingWentWrong } from '../ErrorPage/SomethingWentWrong';
import { NestedModals } from '../NestedRoute/NestedModals';
import { useScrollPositionContext } from '../ScrollPositionProvider/ScrollPositionContext';
import { ModuleWrapper } from './ModuleWrapper/ModuleWrapper';
import { useDefaultReseller } from './useDefaultReseller';
import { VerticalHero } from './VerticalHero/VerticalHero';
import { createLinkElements } from 'utils/PageAssemblyUtil';
import { changePhoneNumberDynamicallyAfterTimeout } from 'utils/InvocaUtils';
import { useInvocaRunByBreakpoints } from 'hooks/useInvocaRunByBreakpoints';

interface IPageAssemblyProps extends HTMLAttributes<HTMLDivElement> {
  entry?: ReferencedEntry<Page>;
}

const PageAssembly: React.FC<IPageAssemblyProps> = props => {
  const { entry } = props;

  // All page types should have an 'items' field with an array of all dynamic modules
  const {
    backgroundColor,
    heroTopic,
    items = [],
    sidebar,
    pageMetadata,
    verticalMarket,
    alternatingModuleColor,
    referralId,
    referralIdDev,
  } = entry?.fields || {};

  useDefaultReseller(referralId, referralIdDev);
  useSidebarOverride(sidebar);
  useInvocaRunByBreakpoints();

  const { setHasPageLoaded } = useScrollPositionContext();
  const { setActivePage } = useContentEntriesContext();

  const contentTypeId = getContentType(entry) as ContentType;

  useEffect(() => {
    setHasPageLoaded(true);
    targetView();
    changePhoneNumberDynamicallyAfterTimeout();

    if (entry) {
      setActivePage(flattenContentfulData(entry));
    }

    return () => {
      setHasPageLoaded(false);
      setActivePage(undefined);
    };
  }, [entry]);

  const componentWithMainHeading = useMemo(() => {
    // use this black list to exclude content types from being considered as main heading
    const blackList = [ContentType.DEVICES_MENU, ContentType.PILL_TABS_MODULE];

    return items.find(i => {
      const contentType = getContentType(i);
      return contentType && !blackList.includes(contentType);
    });
  }, [items]);

  const modules = useMemo(
    () =>
      items.map((item, index) => {
        const isMainHeading = !heroTopic && componentWithMainHeading?.sys.id === item.sys.id;

        const entryWithMetadata: ReferencedEntry<Assembly> = {
          ...item,
          fields: {
            ...item.fields,
            headingTagName: isMainHeading ? 'h1' : 'h2',
          },
        };

        return <ModuleWrapper key={componentKey(item, index)} entry={entryWithMetadata} />;
      }),
    [items, heroTopic, componentWithMainHeading],
  );

  if (!isEntry(entry)) return <SomethingWentWrong />;

  if (ContentType.BASIC_PAGE !== contentTypeId) {
    return <NotFoundPage />;
  }

  const canonicalTag = getField(entry, 'canonicalTag');
  const hrefLangTagList = createLinkElements(
    getField(entry, 'hrefLangLinks'),
    getField(entry, 'hrefLangXDefaultLink'),
  );

  const shouldPreventCrawling = Environment.current.isDev || Environment.current.isSandboxprodEnvironment;

  return (
    <article
      className={cx(styles.pageAssembly, alternatingModuleColor && styles.pageAssemblyAlternatingModuleColor)}
      style={{ backgroundColor, '--alternating-module-color': alternatingModuleColor } as CSSProperties}
      data-entry-id={entry.sys.id}
      data-content-type={entry.sys.contentType.sys.id}
    >
      <Helmet>
        {pageMetadata?.fields?.title && <title>{pageMetadata.fields.title}</title>}
        {pageMetadata?.fields?.description && (
          <meta name="description" content={pageMetadata.fields.description} />
        )}
        {canonicalTag && <link rel="canonical" href={ensureNoTrailingSlash(canonicalTag)} />}
        {shouldPreventCrawling && <meta name="robots" content="noindex" />}
        {hrefLangTagList}
      </Helmet>
      {!verticalMarket ? (
        <HeroModule heroTopic={heroTopic} isHomepage={getField(entry, 'slug') === '/'} />
      ) : (
        <VerticalHero entry={heroTopic} />
      )}
      {modules}
      <NestedModals />
    </article>
  );
};

const WrappedComponent = applyEntryStyleAttributes(PageAssembly);

export { WrappedComponent as PageAssembly };
